/**
 * @fileoverview Main application component for Onicorn with TanStack Router
 * @module App
 */

import type { ReactElement } from 'react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Providers } from '@/lib/providers';
import { useEnhancedAuth } from '@/lib/auth';
import { routeTree } from './routeTree.gen';
import { Toaster } from '@/components/ui/feedback/toaster';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import './App.css';

/**
 * Router configuration with authentication context.
 *
 * Creates a router instance with the generated route tree and
 * authentication context support for protected routes.
 *
 * Note: No defaultPendingComponent - each route handles its own loading state.
 * This prevents jarring sequential loading states (spinner → skeleton → content).
 */
const router = createRouter({
  routeTree,
});


/**
 * Inner app component that provides authentication context to the router.
 *
 * This component must be wrapped with the Web3 providers to access
 * wallet connection state through the useAuth hook.
 *
 * @component
 */
function InnerApp(): ReactElement {
  const auth = useEnhancedAuth();

  return (
    <>
      <RouterProvider router={router} context={{ auth }} />
      <Toaster />
      <SpeedInsights />
      <Analytics />
    </>
  );
}

/**
 * Main application component with TanStack Router integration.
 *
 * Provides the complete application setup with:
 * - Web3 providers (wagmi, RainbowKit, TanStack Query)
 * - TanStack Router with authentication context
 * - Type-safe routing with protected routes
 *
 * @component
 * @example
 * ```tsx
 * <App />
 * ```
 */
function App(): ReactElement {
  return (
    <Providers>
      <InnerApp />
    </Providers>
  );
}

export default App
